import React, { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useUser } from "./UserContext";
import { setActiveMerchantIdHeader, getMyMerchant } from "@api";
import { queryClient } from "@api/queryClient";

const MerchantContext = createContext();
const PaymentSettingsContext = createContext();

export const useMerchant = () => {
  const context = useContext(MerchantContext);

  if (context === undefined) {
    throw new Error("useMerchant must be used within a MerchantProvider");
  }

  return context;
};

export const usePaymentSettings = () => {
  const context = useContext(PaymentSettingsContext);

  if (context === undefined) {
    throw new Error(
      "usePaymentSettings must be used within a MerchantProvider",
    );
  }

  return context;
};

const splitMerchant = (full) => {
  if (!full || typeof full !== "object" || Array.isArray(full)) {
    return { merchant: null, paymentSettings: null };
  }
  const { paymentSettings = null, ...rest } = full;
  return { merchant: rest, paymentSettings };
};

export const MerchantProvider = ({ children }) => {
  const { user } = useUser();
  const [memberships, setMemberships] = useState([]);
  const [merchant, setMerchant] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyMerchant = (full) => {
    const split = splitMerchant(full);
    setMerchant(split.merchant);
    setPaymentSettings(split.paymentSettings);
  };

  const loadMerchants = async () => {
    try {
      if (user?.memberships) {
        console.log(user.memberships);
        setMemberships(user.memberships);
        if (!merchant && user.memberships.length > 0) {
          const storedMerchantId = localStorage.getItem("activeMerchantId");
          let selectedMembership;

          if (storedMerchantId) {
            selectedMembership = user.memberships.find(
              (membership) => String(membership.merchantId) === storedMerchantId,
            );
            console.log(
              "Found stored merchant ID in local storage:",
              storedMerchantId,
            );
          }

          if (!selectedMembership) {
            selectedMembership = user.memberships[0];
            console.log("Using first membership for merchant context");
          } else {
            console.log("Using stored membership for merchant context");
          }

          setActiveMerchantIdHeader(selectedMembership.merchantId);
          const full = await getMyMerchant({ includeContractDetails: true });
          console.log("loaded merchant", full);

          applyMerchant(full);
        }
      }
    } catch (error) {
      console.error("Failed to load merchants:", error);
    } finally {
      console.log("Finished loading merchants");
      setLoading(false);
    }
  };

  const switchMerchant = async (membership) => {
    localStorage.setItem("activeMerchantId", membership.merchantId);
    setActiveMerchantIdHeader(membership.merchantId);
    queryClient.clear();
    const full = await getMyMerchant({ includeContractDetails: true });
    applyMerchant(full);
  };

  const reload = async () => {
    await loadMerchants();
  };

  const reloadPaymentSettings = async () => {
    const full = await getMyMerchant({ includeContractDetails: true });
    const next = splitMerchant(full).paymentSettings;
    if (!next) return null;
    setPaymentSettings(next);
    return next;
  };

  const hasPermission = (permission) => {
    return (
      memberships
        ?.find((x) => String(x.merchantId) === String(merchant?.id))
        ?.permissions?.includes(permission) || false
    );
  };

  const hasModule = (module) => {
    return merchant?.modules?.includes(module) || false;
  };

  const branchScopingEnabled =
    memberships?.find((x) => String(x.merchantId) === String(merchant?.id))
      ?.branchScopingEnabled || false;

  useEffect(() => {
    if (user) {
      loadMerchants();
    }
  }, [user]);

  const merchantValue = useMemo(
    () => ({
      memberships,
      merchant,
      loading,
      switchMerchant,
      reload,
      hasPermission,
      hasModule,
      branchScopingEnabled,
    }),
    [memberships, merchant, loading, user],
  );

  const paymentSettingsValue = useMemo(
    () => ({
      paymentSettings,
      reloadPaymentSettings,
    }),
    [paymentSettings],
  );

  return (
    <MerchantContext.Provider value={merchantValue}>
      <PaymentSettingsContext.Provider value={paymentSettingsValue}>
        {children}
      </PaymentSettingsContext.Provider>
    </MerchantContext.Provider>
  );
};
